import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Lock, Crown, Sparkles, X, Gem } from "lucide-react";

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const [tier, setTier] = useState(null); // null = loading
  const [isPro, setIsPro] = useState(false);
  const [isDiamond, setIsDiamond] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const intervalRef = useRef(null);

  const fetchTier = useCallback(async () => {
    // Only fetch if user has a token (is authenticated)
    const token = sessionStorage.getItem("lumifit_session_token");
    if (!token) {
      setTier("free");
      setIsPro(false);
      setIsDiamond(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/subscription/tier`);
      setTier(res.data.tier);
      setIsPro(res.data.is_pro);
      setIsDiamond(res.data.is_diamond || false);
      setIsTrial(res.data.is_trial || false);
      setExpiresAt(res.data.expires_at || null);
    } catch {
      setTier("free");
      setIsPro(false);
      setIsDiamond(false);
    }
  }, []);

  useEffect(() => {
    fetchTier();

    // Poll for token changes (handles login/logout without prop drilling)
    let lastToken = sessionStorage.getItem("lumifit_session_token");
    intervalRef.current = setInterval(() => {
      const currentToken = sessionStorage.getItem("lumifit_session_token");
      if (currentToken !== lastToken) {
        lastToken = currentToken;
        fetchTier();
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchTier]);

  return (
    <SubscriptionContext.Provider value={{ tier, isPro, isDiamond, isTrial, expiresAt, refreshTier: fetchTier }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) return { tier: "free", isPro: false, isDiamond: false, isTrial: false, expiresAt: null, refreshTier: () => {} };
  return ctx;
};

// Premium gate component — wraps features that require Pro or Diamond
export const PremiumGate = ({ children, feature, requiredTier = "pro" }) => {
  const { isPro, isDiamond, tier } = useSubscription();
  const [showPrompt, setShowPrompt] = useState(false);

  const needsDiamond = requiredTier === "diamond";
  const hasAccess = needsDiamond ? isDiamond : isPro;

  // While tier is loading, show content without paywall
  if (tier === null || hasAccess) return children;

  return (
    <>
      <div
        className="relative cursor-pointer group"
        onClick={() => setShowPrompt(true)}
        data-testid={`premium-gate-${feature}`}
      >
        <div className="pointer-events-none opacity-40 blur-[1px] select-none">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-xl px-5 py-3 shadow-lg flex items-center gap-2 group-hover:scale-105 transition-transform">
            <Lock className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium text-slate-700">{needsDiamond ? "Diamond Feature" : "Pro Feature"}</span>
            {needsDiamond ? <Gem className="w-4 h-4 text-violet-500" /> : <Crown className="w-4 h-4 text-amber-500" />}
          </div>
        </div>
      </div>
      {showPrompt && <UpgradePrompt feature={feature} onClose={() => setShowPrompt(false)} requiredTier={requiredTier} />}
    </>
  );
};

// Upgrade prompt modal
const UpgradePrompt = ({ feature, onClose, requiredTier = "pro" }) => {
  const navigate = useNavigate();
  const needsDiamond = requiredTier === "diamond";

  const featureLabels = {
    "deep-dive": "Deep Dive Clinical Assessments",
    "workout-lab": "Workout Lab & Custom Exercises",
    "all-games": "All Brain Games & Difficulties",
    "weekly-report": "Weekly AI Reports",
    "mindfulness-full": "Full Mindfulness Library",
    "leaderboard-rank": "Leaderboard Ranking",
    "profile-share": "Shareable Profile Card",
    "sleep-stories": "AI Sleep Stories",
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="upgrade-prompt">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        <div className="p-6 text-center">
          <button onClick={onClose} className="absolute top-4 right-4 text-slate-300 hover:text-slate-500" data-testid="close-upgrade-btn">
            <X className="w-5 h-5" />
          </button>
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${needsDiamond ? "bg-violet-100" : "bg-amber-100"}`}>
            {needsDiamond ? <Gem className="w-8 h-8 text-violet-500" /> : <Crown className="w-8 h-8 text-amber-500" />}
          </div>
          <h3 className="text-xl font-light text-sky-900 mb-2" style={{ fontFamily: "Outfit" }}>
            Unlock {featureLabels[feature] || (needsDiamond ? "Diamond Features" : "Pro Features")}
          </h3>
          <p className="text-sm text-slate-500 mb-6">
            {needsDiamond
              ? "Upgrade to Diamond for the complete LumiFit experience including Sleep Stories and Deep Dive."
              : <>Upgrade to Pro for unlimited access to all LumiFit features. Start with a <strong>7-day free trial</strong>.</>}
          </p>
          <div className="space-y-2">
            <Button
              onClick={() => { onClose(); navigate("/subscription"); }}
              className={`w-full ${needsDiamond ? "bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white" : "glow-button"}`}
              data-testid="upgrade-btn"
            >
              {needsDiamond ? <><Gem className="w-4 h-4 mr-2" /> Upgrade to Diamond</> : <><Sparkles className="w-4 h-4 mr-2" /> Start Free Trial</>}
            </Button>
            <Button variant="ghost" onClick={onClose} className="w-full text-slate-400">
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple pro badge for display
export const ProBadge = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium ${className}`} data-testid="pro-badge">
    <Crown className="w-3 h-3" /> PRO
  </span>
);
